import { ButtonGroupProps, CardType, RichTextType, FoodType, RestaurantType, ButtonProps, AssetType } from '~types'
import { createButton } from 'utils/storybook/createButton'

interface BaseContentType {
  id?: string
  componentName?: string
  headline?: string
  description?: RichTextType
  image?: AssetType
}

type FoodMapToCardParams = {
  item: FoodType
  options?: {
    openInNewTab: boolean
  }
}

type RestaurantMapToCardParams = {
  item: RestaurantType
  options?: {
    openInNewTab: boolean
    customButtonText: {
      menu?: string
      location?: string
    }
    buttonColors: {
      menu?: ButtonProps['color']
      location?: ButtonProps['color']
    }
  }
}

export type RestaurantWithCtas = RestaurantType & {
  buttonGroup: ButtonGroupProps
}

type LinkDataType = { url?: string | null; text?: string | null }[]

type DefaultMapToCardParams = {
  item: BaseContentType
  ctas: ButtonProps[]
  buttonGroup: ButtonGroupProps
}

// Union type for all possible parameters
type MapToCardParams = FoodMapToCardParams | RestaurantMapToCardParams | DefaultMapToCardParams

interface ComponentConfig {
  buttonGroupHeadline?: string
  extractLinks: (item: any) => LinkDataType
  mapToCard: (params: any) => Partial<CardType>
}

export interface NormalizeOptions {
  openInNewTab?: boolean
  buttonGroupHeadline?: string
  linkSeparator?: string
  // Restaurant-specific options
  customButtonText?: {
    menu?: string
    location?: string
  }
  buttonColors?: {
    menu?: ButtonProps['color']
    location?: ButtonProps['color']
  }
  // Allow overriding the default configuration
  overrides?: {
    [componentName: string]: Partial<ComponentConfig>
  }
}

const isFoodType = (item: any): item is FoodType => item.componentName === 'Food'
const isRestaurantType = (item: any): item is RestaurantType => item.componentName === 'Restaurant'

// Split taxonomies strings into separate words
// Input:  "Section100",  "section100",  "EventLevel",  "LowerGallerySection300S400S"
// Output: "Section 100", "Section 100", "Event Level", "Lower Gallery Section 300S 400S"
const splitTaxonomyValue = (taxonomyValue: string): string => {
  // Match capital letter followed by lowercase letters, or numbers followed by optional 'S'
  const words = taxonomyValue.match(/[A-Z][a-z]+|[0-9]+S?/g) || []
  return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}

// Configuration for each component type with proper typing
const componentConfigs: Record<string, ComponentConfig> = {
  Food: {
    extractLinks: (item: FoodType) => {
      return (
        item.restaurants?.map(restaurant => ({
          url: restaurant.mapLocation?.url,
          text: restaurant.headline
        })) || []
      )
    },
    mapToCard({ item, options }: FoodMapToCardParams) {
      const { openInNewTab } = options || {}
      const linkData = this?.extractLinks(item)
      let ctas: ButtonProps[] = []

      if (linkData?.length) {
        ctas = linkData.map((link, index) =>
          createButton({
            id: `${item.id}-food-${index}`,
            title: link.text!,
            isLink: true,
            isTargetBlank: openInNewTab,
            modal: {
              view: {
                componentName: 'ModalViewMedia',
                mediaAsset: item?.restaurants[index]?.mapLocation as AssetType
              },
              backgroundColor: 'gray-900',
              width: 'content-aware'
            }
          })
        )
      }

      // Construct buttonGroup
      const buttonGroup = {
        buttons: ctas.length > 0 ? ctas : undefined,
        headline: 'Where to find:',
        layout: 'inline' as const,
        analytics: {
          linkModule: 'card',
          parentHeadline: item.headline
        }
      }

      return {
        ...item,
        buttonGroup
      }
    }
  },
  Restaurant: {
    extractLinks: () => [],
    mapToCard: ({ item, options }: RestaurantMapToCardParams) => {
      const { openInNewTab = false, customButtonText = {}, buttonColors = {} } = options || {}
      const ctas: ButtonProps[] = []

      const validMenus = item.menus?.filter(menu => menu.image?.url) || []

      // Menu button - now handles both single and multiple menus
      if (validMenus.length > 0) {
        const menuButtonProps = {
          id: `${item.id}-restaurant`,
          title: customButtonText.menu || 'View Menu',
          color: buttonColors.menu || 'light',
          fill: 'solid',
          isTargetBlank: openInNewTab,
          analytics: {
            linkModule: 'restaurant',
            clickTracking: `restaurant:${item.headline}:view-menu:cta`
          },
          // Single menu - open modal directly
          ...(validMenus.length === 1 &&
            ({
              modal: {
                id: `${validMenus[0].id}-artist-menu`,
                view: {
                  componentName: 'ModalViewMedia',
                  mediaAsset: validMenus[0].image
                },
                backgroundColor: 'gray-900',
                width: 'content-aware'
              }
            } as const)),
          // Multiple menus - use dropdown
          ...(validMenus.length > 1 && {
            dropdownOptions: validMenus.map(menu => ({
              value: menu.id,
              label: menu.artist?.artistName ? `${menu.artist?.artistName} menu` : 'Menu',
              mediaAsset: menu.image,
              restaurantId: item.id,
              artistId: menu?.artist?.id
            })),
            icon: {
              iconName: 'chevron_down',
              title: 'Chevron down'
            } as const
          })
        }

        ctas.push(menuButtonProps)
      }

      // Location button
      if (item.mapLocation?.url) {
        ctas.push(
          createButton({
            id: `${item.id}-location`,
            title: '',
            color: buttonColors.location || 'light',
            fill: 'hollow',
            isTargetBlank: openInNewTab,
            analytics: {
              linkModule: 'restaurant',
              clickTracking: `restaurant:${item.headline}:view-location:cta`
            },
            icon: {
              iconName: 'map',
              title: 'Map'
            },
            modal: {
              view: {
                componentName: 'ModalViewMedia',
                mediaAsset: item.mapLocation as AssetType
              },
              width: 'content-aware',
              backgroundColor: 'gray-900'
            }
          })
        )
      }

      // Add eyebrow with section or location info if available
      if (item.restaurantLocation && item.restaurantLocation.length > 0) {
        // Find the shortest section location (e.g., prefer "Section100" over "MainLevelSection100S")
        const sectionLocation = item.restaurantLocation
          .filter(loc => loc.value?.toLowerCase().includes('section'))
          .sort((a, b) => (a.value?.length || 0) - (b.value?.length || 0))[0]

        // Use section location if found, otherwise use first location
        const locationValue = sectionLocation?.value || item.restaurantLocation[0].value

        item.eyebrow = {
          title: splitTaxonomyValue(locationValue),
          backgroundColor: 'primary',
          isCornerTag: true,
          size: 'md',
          isBold: true
        }
      }

      return {
        ...item,
        buttonGroup: {
          buttons: ctas.length > 0 ? ctas : undefined,
          layout: 'inline',
          isFullWidthOverride: false
        }
      }
    }
  },
  // Add Default config for unknown component types
  Default: {
    buttonGroupHeadline: '',
    extractLinks: (item: any) => [{ url: item.url, text: item.headline || 'View Details' }].filter(link => link.url),
    mapToCard: (params: DefaultMapToCardParams) => ({
      ...params.item,
      buttonGroup: {
        buttons: params.ctas,
        layout: 'inline'
      }
    })
  }
}

/**
 * Normalizes data based on componentName property
 */
export function normalizeDataToCard(data: BaseContentType[] = [], options: NormalizeOptions = {}): CardType[] {
  const { openInNewTab = true, buttonGroupHeadline, customButtonText = {}, buttonColors = {}, overrides = {} } = options

  return data.map(item => {
    const componentName = item.componentName as keyof typeof componentConfigs
    const config = componentConfigs[componentName] || componentConfigs.Default
    const overrideConfig = overrides[componentName] || {}

    const finalConfig: ComponentConfig = {
      ...config,
      ...overrideConfig,
      buttonGroupHeadline: buttonGroupHeadline || overrideConfig.buttonGroupHeadline || config.buttonGroupHeadline
    }

    // Build mapToCard params
    let mapToCardParams: MapToCardParams

    if (isRestaurantType(item)) {
      mapToCardParams = {
        item,
        options: { openInNewTab, customButtonText, buttonColors }
      }
    } else if (isFoodType(item)) {
      mapToCardParams = {
        item,
        options: { openInNewTab }
      }
    } else {
      mapToCardParams = {
        item
      }
    }

    const cardData = finalConfig.mapToCard(mapToCardParams)

    return {
      ...cardData
    } as CardType
  })
}
